import type { Moment } from "@coaching/types";
import { holdFor } from "@coaching/annotations";
import { clock } from "../format";
import { Icon } from "./icons";
import { useImprove } from "./Improve";

/** "Clip 2 · 0:12" for a moment, from the bucket's clip order. */
export function momentPlace(m: Moment, clipIds: readonly string[]): string {
    const i = clipIds.indexOf(m.clipId);
    return `${i >= 0 ? `Clip ${i + 1} · ` : ""}${clock(m.t)}`;
}

export function MomentList({ moments, clipIds, activeId, onSelect }: { moments: Moment[]; clipIds: readonly string[]; activeId: string | null; onSelect: (m: Moment) => void }) {
    if (moments.length === 0) {
        return (
            <div className="moments-empty">
                <p><strong>No moments yet.</strong></p>
                <p className="muted small">Pause where you want to make a point, then draw on the frame or press <kbd>M</kbd>.</p>
            </div>
        );
    }
    return (
        <ol className="moments">
            {moments.map((m, i) => (
                <li key={m.id}>
                    <button type="button" className={`moment${m.id === activeId ? " on" : ""}`} onClick={() => onSelect(m)}
                        style={{ "--pin": m.shapes[0]?.color ?? "var(--accent)" } as React.CSSProperties}>
                        <span className="moment-index">{i + 1}</span>
                        <span className="moment-body">
                            <span className="moment-title">{m.label || `Moment ${i + 1}`}</span>
                            <span className="moment-meta">
                                <span className="mono">{momentPlace(m, clipIds)}</span>
                                {m.shapes.length > 0 && <span title={`${m.shapes.length} drawn`}><Icon.shapes /> {m.shapes.length}</span>}
                                {m.note && <span title="Has a note"><Icon.note /></span>}
                                {m.voice && <span title={`Voice note, ${clock(m.voice.durationSec)}`}><Icon.voice /> {clock(m.voice.durationSec)}</span>}
                            </span>
                        </span>
                    </button>
                </li>
            ))}
        </ol>
    );
}

export function MomentEditor({ moment, clipIds, editable, improve, onChange, onDelete }: {
    moment: Moment;
    clipIds: readonly string[];
    editable: boolean;
    /** Develops the note into fuller feedback; absent when Improve is off. */
    improve?: (text: string, momentTitle: string) => Promise<string>;
    onChange: (patch: Partial<Moment>) => void;
    onDelete: () => void;
}) {
    const assist = useImprove(moment.note, (t) => (improve ? improve(t, moment.label) : Promise.reject(new Error("Improve is off"))), (note) => onChange({ note }));
    const voice = moment.voice ?? null;
    const held = holdFor(moment);
    return (
        <div className="moment-editor">
            <div className="moment-editor-head">
                <span className="chip mono">{momentPlace(moment, clipIds)}</span>
                <label className="hold">
                    <span>Hold</span>
                    <span className="select-wrap">
                        <select value={moment.holdSec} disabled={!editable} onChange={(e) => onChange({ holdSec: Number(e.target.value) })}>
                            {[2, 3, 4, 6, 8, 12].map((s) => <option key={s} value={s}>{s}s</option>)}
                        </select>
                    </span>
                </label>
                {editable && <button type="button" className="icon-btn danger" onClick={onDelete} title="Delete this moment" aria-label="Delete this moment"><Icon.trash /></button>}
            </div>
            {voice && held > moment.holdSec && <p className="muted small hold-note">The frame holds for {clock(held)} while your voice note plays.</p>}
            <label>Title
                <input value={moment.label} maxLength={80} disabled={!editable} placeholder="Split step before the volley"
                    onChange={(e) => onChange({ label: e.target.value })} />
            </label>
            <div className="field-block">
                <div className="field-row">
                    <label htmlFor={`note-${moment.id}`}>Note to the player</label>
                    {improve && editable && assist.button(false)}
                </div>
                <textarea id={`note-${moment.id}`} value={moment.note} rows={4} maxLength={1000} disabled={!editable}
                    placeholder="Jot it down quickly: Improve turns it into detailed feedback with a drill."
                    onChange={(e) => onChange({ note: e.target.value })} />
                {assist.panel}
            </div>
            <div className={`voice-status${voice ? " has" : ""}`}>
                <Icon.mic />
                {voice
                    ? <span>Voice note, {clock(voice.durationSec)}{moment.shapes.some((s) => s.at !== undefined) ? ". Drawings appear as you talk." : "."} Play, re-record or remove it under the frame.</span>
                    : <span>{editable ? "Press Talk & draw under the frame to talk the player through this moment. Anything you draw while talking appears as you say it." : "No voice note."}</span>}
            </div>
        </div>
    );
}
